import React, { useEffect, useState } from "react";
import { useIntl, defineMessages } from "react-intl";
import Svg from "@natera/material/lib/svg";
import { RadioButton } from "@natera/material/lib/radioButton";
import Tooltip from "@natera/material/lib/tooltip";
import PaymentPlanIcon from "@app/postBill/assets/svg/payment-plan.svg";
import ChevronUpIcon from "@app/postBill/assets/svg/chevron-up.svg";
import ChevronDownIcon from "@app/postBill/assets/svg/chevron-down.svg";
import "./paymentPlanCard.scss";
import { PaymentPlan } from "@app/postBill/models/apiControllerModel";
import classNames from "classnames";

export const messages = defineMessages({
  paymentPlanCardTitle: {
    id: "paymentPlanCardTitle",
    defaultMessage: "Interest-free payment plan",
  },
  paymentPlanCardSubtitle: {
    id: "paymentPlanCardSubtitle",
    defaultMessage:
      "You can choose an optional payment plan of up to 12 months.",
  },
  paymentPlanCardSubtitleWithPlan: {
    id: "paymentPlanCardSubtitleWithPlan",
    defaultMessage:
      "You’ve selected to pay over the next {paymentPlan} months.",
  },
  paymentPlanCardOption3: {
    id: "paymentPlanCardOption3",
    defaultMessage: "Pay in 3",
  },
  paymentPlanCardOption6: {
    id: "paymentPlanCardOption6",
    defaultMessage: "Pay in 6",
  },
  paymentPlanCardOption12: {
    id: "paymentPlanCardOption12",
    defaultMessage: "Pay in 12",
  },
  paymentPlanCardRemoveButton: {
    id: "paymentPlanCardRemoveButton",
    defaultMessage: "Remove",
  },
  paymentPlanCardTooltip: {
    id: "paymentPlanCardTooltip",
    defaultMessage: "Payment plan is not available for balances under $50",
  },
});

interface PaymentPlanCardProps {
  selectedPlan: number | null;
  onSelectPlan: (value: number) => void;
  onRemovePlan: () => void;
  disabled?: boolean;
}

const PaymentPlanCard: React.FC<PaymentPlanCardProps> = ({
  selectedPlan,
  onSelectPlan,
  onRemovePlan,
  disabled,
}) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => !disabled && setExpanded(!expanded);
  const handleSelect = (value: number) => onSelectPlan(value);
  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemovePlan();
  };

  useEffect(() => {
    if (disabled) {
      setExpanded(false);
    }
  }, [disabled]);

  const paymentPlanOptions = [
    {
      label: intl.formatMessage(messages.paymentPlanCardOption3),
      value: PaymentPlan.ThreeMonths,
    },
    {
      label: intl.formatMessage(messages.paymentPlanCardOption6),
      value: PaymentPlan.SixMonths,
    },
    {
      label: intl.formatMessage(messages.paymentPlanCardOption12),
      value: PaymentPlan.TwelveMonths,
    },
  ];

  return (
    <div
      className={classNames("payment-plan-card", {
        expanded: expanded,
        "payment-plan-card--disabled": disabled,
      })}
      onClick={toggleExpand}
    >
      <div className="payment-plan-card__header">
        <div className="payment-plan-card__icon">
          <Svg content={PaymentPlanIcon} />
        </div>
        <div className="payment-plan-card__info">
          <div className="payment-plan-card__title">
            {intl.formatMessage(messages.paymentPlanCardTitle)}
          </div>
          <div className="payment-plan-card__description">
            {selectedPlan !== PaymentPlan.None
              ? intl.formatMessage(messages.paymentPlanCardSubtitleWithPlan, {
                  paymentPlan: selectedPlan,
                })
              : intl.formatMessage(messages.paymentPlanCardSubtitle)}
          </div>
        </div>
        <div
          className="payment-plan-card__expand-icon"
          onClick={(e) => disabled && e.stopPropagation()}
        >
          {disabled ? (
            <Tooltip
              content={intl.formatMessage(messages.paymentPlanCardTooltip)}
              position={{ xPos: 1 }}
            >
              <div className="payment-plan-card__tooltip">?</div>
            </Tooltip>
          ) : (
            <Svg content={expanded ? ChevronUpIcon : ChevronDownIcon} />
          )}
        </div>
      </div>

      <div
        className="payment-plan-card__body"
        onClick={(e) => e.stopPropagation()}
      >
        {paymentPlanOptions.map((paymentPlanOption) => (
          <div
            key={paymentPlanOption.value}
            className="payment-plan-card__option"
          >
            <RadioButton
              name="payment-plan"
              id={paymentPlanOption.value.toString()}
              label={paymentPlanOption.label}
              checked={selectedPlan === paymentPlanOption.value}
              onChange={() => handleSelect(paymentPlanOption.value)}
            />
            {selectedPlan === paymentPlanOption.value && (
              <button
                className="payment-plan-card__remove"
                onClick={handleRemove}
              >
                {intl.formatMessage(messages.paymentPlanCardRemoveButton)}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentPlanCard;
